import { cents } from '../money/index.js';
import { failure, success } from '../types.js';
import type { Cents, PoolKind, Result } from '../types.js';

/**
 * A discretionary pool: a sector's or the Executives'. Their awards are charged to the pool's own
 * project (PRD §8) and count toward the discretionary budget. The PAA/TAA pool and the Spot Awards
 * pool (APP-264) are neither: their awards use their own account maps, with no project.
 */
export function isDiscretionaryPool(pool: { readonly kind: PoolKind }): boolean {
  return pool.kind === 'sector' || pool.kind === 'executive';
}

export interface UnitAmounts {
  readonly cashCents: number;
  readonly options: number;
}
export type BudgetValue =
  | { readonly status: 'not_set' }
  | { readonly status: 'set'; readonly cashCents: number; readonly options: number };
export interface LedgerEntry extends UnitAmounts {
  readonly id: string;
  readonly fiscalYear: number;
  readonly kind: 'initial' | 'addition' | 'reduction' | 'move' | 'top_up';
  readonly poolId?: string;
  readonly fromPoolId?: string;
  readonly toPoolId?: string;
  readonly voided: boolean;
}
const ZERO: UnitAmounts = { cashCents: 0, options: 0 };
const LIMIT = BigInt(Number.MAX_SAFE_INTEGER);
function validUnits(value: UnitAmounts, signed = false): boolean {
  return (
    Number.isSafeInteger(value.cashCents) &&
    Number.isSafeInteger(value.options) &&
    (signed || (value.cashCents >= 0 && value.options >= 0))
  );
}
function safeUnits(
  cash: bigint,
  options: bigint,
): Result<{ readonly cashCents: Cents; readonly options: number }> {
  if (cash < -LIMIT || cash > LIMIT || options < -LIMIT || options > LIMIT)
    return failure('amount_overflow');
  return success({ cashCents: cents(Number(cash)), options: Number(options) });
}
function ownValue<T>(record: Readonly<Record<string, T>>, key: string): T | undefined {
  return Object.hasOwn(record, key) ? record[key] : undefined;
}

export function budgetFor(
  poolId: string,
  fiscalYear: number,
  entries: readonly LedgerEntry[],
): Result<BudgetValue> {
  if (!poolId.trim() || !Number.isSafeInteger(fiscalYear) || fiscalYear < 1)
    return failure('invalid_budget_scope');
  let cash = 0n;
  let options = 0n;
  let hasEntries = false;
  const seen = new Set<string>();
  for (const entry of entries) {
    if (entry.fiscalYear !== fiscalYear) continue;
    if (!entry.id.trim() || seen.has(entry.id)) return failure('duplicate_ledger_entry');
    seen.add(entry.id);
    if (entry.voided) continue;
    if (!validUnits(entry)) return failure('invalid_ledger_amount');
    let sign = 0n;
    if (entry.kind === 'move') {
      if (
        !entry.fromPoolId?.trim() ||
        !entry.toPoolId?.trim() ||
        entry.fromPoolId === entry.toPoolId
      )
        return failure('invalid_budget_move');
      sign = entry.fromPoolId === poolId ? -1n : entry.toPoolId === poolId ? 1n : 0n;
    } else {
      if (!entry.poolId?.trim()) return failure('missing_funding_pool');
      if (!['initial', 'addition', 'reduction', 'top_up'].includes(entry.kind))
        return failure('invalid_ledger_kind');
      if (entry.poolId === poolId) sign = entry.kind === 'reduction' ? -1n : 1n;
    }
    if (sign !== 0n) {
      hasEntries = true;
      cash += sign * BigInt(entry.cashCents);
      options += sign * BigInt(entry.options);
    }
  }
  if (!hasEntries) return success({ status: 'not_set' });
  const amounts = safeUnits(cash, options);
  return amounts.ok ? success({ status: 'set', ...amounts.value }) : amounts;
}

export interface BudgetUsage {
  readonly inProgress: UnitAmounts;
  readonly paid: UnitAmounts;
}
export interface BudgetLine extends UnitAmounts {
  readonly id: string;
  readonly poolId: string;
  readonly status: 'held' | 'settled' | 'released';
}
export function usageFor(
  poolId: string,
  lines: readonly BudgetLine[],
): Result<BudgetUsage & { readonly awarded: UnitAmounts }> {
  let heldCash = 0n;
  let heldOptions = 0n;
  let paidCash = 0n;
  let paidOptions = 0n;
  const seen = new Set<string>();
  for (const line of lines) {
    if (!line.id.trim() || seen.has(line.id)) return failure('duplicate_budget_line');
    seen.add(line.id);
    if (line.poolId !== poolId) continue;
    if (!validUnits(line)) return failure('invalid_budget_usage');
    if (line.status === 'held') {
      heldCash += BigInt(line.cashCents);
      heldOptions += BigInt(line.options);
    } else if (line.status === 'settled') {
      paidCash += BigInt(line.cashCents);
      paidOptions += BigInt(line.options);
    }
  }
  const inProgress = safeUnits(heldCash, heldOptions);
  const paid = safeUnits(paidCash, paidOptions);
  const awarded = safeUnits(heldCash + paidCash, heldOptions + paidOptions);
  if (!inProgress.ok) return inProgress;
  if (!paid.ok) return paid;
  if (!awarded.ok) return awarded;
  return success({ inProgress: inProgress.value, paid: paid.value, awarded: awarded.value });
}

export interface BudgetCheckInput {
  /** Full replacement demand. The trusted manager supplies the old hold separately. */
  readonly demandByPool: Readonly<Record<string, UnitAmounts>>;
  readonly usageByPool: Readonly<Record<string, BudgetUsage>>;
  readonly budgetByPool: Readonly<Record<string, BudgetValue>>;
  readonly existingHoldByPool?: Readonly<Record<string, UnitAmounts>>;
}
export type PoolBudgetCheck =
  | { readonly poolId: string; readonly status: 'not_set' }
  | {
      readonly poolId: string;
      readonly status: 'ok' | 'over_budget';
      readonly remainingAfter: UnitAmounts;
      readonly shortfall: UnitAmounts;
    };

export function checkBudget(input: BudgetCheckInput): Result<readonly PoolBudgetCheck[]> {
  const checks: PoolBudgetCheck[] = [];
  const poolIds = new Set([
    ...Object.keys(input.demandByPool),
    ...Object.keys(input.existingHoldByPool ?? {}),
  ]);
  for (const poolId of poolIds) {
    const demand = ownValue(input.demandByPool, poolId) ?? ZERO;
    const usage = ownValue(input.usageByPool, poolId) ?? { inProgress: ZERO, paid: ZERO };
    const oldHold = ownValue(input.existingHoldByPool ?? {}, poolId) ?? ZERO;
    if (![demand, usage.inProgress, usage.paid, oldHold].every((amount) => validUnits(amount)))
      return failure('invalid_budget_amount');
    if (
      oldHold.cashCents > usage.inProgress.cashCents ||
      oldHold.options > usage.inProgress.options
    )
      return failure('invalid_existing_hold');
    const budget = ownValue(input.budgetByPool, poolId);
    if (!budget || budget.status === 'not_set') {
      checks.push({ poolId, status: 'not_set' });
      continue;
    }
    if (!validUnits(budget, true)) return failure('invalid_budget_amount');
    const remaining = safeUnits(
      BigInt(budget.cashCents) -
        BigInt(usage.inProgress.cashCents) +
        BigInt(oldHold.cashCents) -
        BigInt(usage.paid.cashCents) -
        BigInt(demand.cashCents),
      BigInt(budget.options) -
        BigInt(usage.inProgress.options) +
        BigInt(oldHold.options) -
        BigInt(usage.paid.options) -
        BigInt(demand.options),
    );
    if (!remaining.ok) return remaining;
    checks.push({
      poolId,
      status: remaining.value.cashCents < 0 || remaining.value.options < 0 ? 'over_budget' : 'ok',
      remainingAfter: remaining.value,
      shortfall: {
        cashCents: Math.max(0, 0 - remaining.value.cashCents),
        options: Math.max(0, -remaining.value.options),
      },
    });
  }
  return success(checks);
}
